import React, { FC, useCallback, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import Page from '@components/Page/Page';
import { copyToClipboard } from "@datassential/platform-ui-lib";
import KeywordSettings from "@components/KeywordSettings";
import TopTrendsMenuExamplePopup from "./TopTrendsMenuExamplePopup";
import useTopTrendsMenuExamples, {
    MainFilter,
    TopTrendsMenuExamplesExportRequest
} from "@apiHooks/useTopTrendsMenuExamples";
import TopTrendsTable from "./TopTrendsTable";
import TopTrendsControls from "./TopTrendsControls";
import FooterContent from "./FooterContent";
import { IconCopySimple } from "@icons";
import ActionIcon from "@components/ActionIcon";
import TableInfo from "@components/TableInfo/TableInfo";
import { TopTrendsProvider, useTopTrends } from "./TopTrendsContext";
import { toast } from "react-toastify";
import AiResultSection from '@vms/AiResult/AiResultSection';
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import { useAiSearchTopTrendsSettings } from "@pages/TopTrends/useAiSearchTopTrendsSettings";
import { useDefaultFilterData, useFilters } from "@vms/SelectFilters/SelectFiltersContext";

const TopTrendsContent: FC = () => {
    const [isShown, setIsShown] = useState(false);
    const { countryCode } = useFilters();
    const defaultFilterData = useDefaultFilterData();

    const {
        totalRecords,
        backendFilters,
        showOnlyNewItems,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        growthPeriod,
        drillWords,
        onDeleteDrillWord,
        keywordSettingsState,
        onCloseKeywordSettings,
        menuExamplesWordData,
        isMenuExamplesPopupOpened,
        onCloseMenuExamplesPopup,
        copyTableContent,
    } = useTopTrends();

    useEffect(() => {
        setIsShown(true);
    }, []);

    const menuExamplesMainFilter: MainFilter = {
        filters: backendFilters,
        includeLessOnePercentPenetration: includeLowPenetrationItems,
        includeLessTenMenusCount: includeLessTenMenusCount,
        isNew: showOnlyNewItems,
        yearsGrowth: growthPeriod,
        ...(drillWords.length > 0 ? { drillWords } : {}),
    };

    const {
        fetch: fetchMenuExamples,
        getExportData,
    } = useTopTrendsMenuExamples({
        word: menuExamplesWordData,
        mainFilter: menuExamplesMainFilter,
        fullFilter: { ...defaultFilterData, countryCode } as TopTrendsMenuExamplesExportRequest['fullFilter'],
    });

    const handleCopyTable = useCallback(() => {
        if (copyTableContent) {
            copyToClipboard(copyTableContent);
            toast.success("Successfully copied to your clipboard");
        } else {
            toast.error("Something went wrong");
        }

    }, [copyTableContent]);

    return (
        <>
            <CSSTransition in={isShown} classNames="appears1" timeout={3000} unmountOnExit>
                <div className="TopTrends__table-info-row">
                    <TableInfo
                        className="TopTrends__table-info"
                        drillWords={drillWords}
                        totalRecords={totalRecords}
                        handleDeleteItem={onDeleteDrillWord}
                    />
                </div>
            </CSSTransition>

            <CSSTransition in={isShown} classNames="appears1" timeout={3000} unmountOnExit>
                <TopTrendsControls/>
            </CSSTransition>

            <CSSTransition in={isShown} classNames="appears2" timeout={3000} unmountOnExit>
                <div className="TopTrends__content">
                    <TopTrendsTable/>

                    <div className="TopTrendsTable__copy-table-btn">
                        <ActionIcon
                            tooltip="copy table"
                            onClick={handleCopyTable}
                        >
                            <IconCopySimple/>
                        </ActionIcon>
                    </div>
                </div>
            </CSSTransition>

            <div className="TopTrends__footer">
                <FooterContent/>
            </div>

            <KeywordSettings
                shown={keywordSettingsState.isSettingsShown}
                x={keywordSettingsState.settingsX}
                y={keywordSettingsState.settingsY}
                searchingKeyword={keywordSettingsState.keyword?.name ?? ''}
                readOnly={true}
                closeModal={onCloseKeywordSettings}
                includes={(keywordSettingsState.keyword?.wordExtensions ?? []).filter(
                    item => item.wordType === 1 || item.wordType === 3
                )}
                excludes={(keywordSettingsState.keyword?.wordExtensions ?? []).filter(
                    item => item.wordType === 2
                )}
                keywords={[]}
                id={keywordSettingsState.keyword?.id ?? 0}
            />

            <TopTrendsMenuExamplePopup
                isShown={isMenuExamplesPopupOpened}
                close={onCloseMenuExamplesPopup}
                fetch={fetchMenuExamples}
                getExportData={getExportData}
            />
        </>
    );
};

const TopTrends: FC = () => {
    const {
        isLoading: isAiSearchSettingsLoading,
        isActive: isAiSearchActive,
    } = useAiSearchSettingsContext();

    const aiSearchSettings = useAiSearchTopTrendsSettings();

    return (
        <Page title="Top Trends">
            <div className="TopTrends">
                <CSSTransition
                    in={isAiSearchActive}
                    classNames="appears"
                    timeout={500}
                    unmountOnExit
                >
                    <AiResultSection
                        settings={aiSearchSettings}
                        isSettingsLoading={isAiSearchSettingsLoading}
                    />
                </CSSTransition>
                {!isAiSearchSettingsLoading && (
                    <TopTrendsProvider>
                        <TopTrendsContent/>
                    </TopTrendsProvider>
                )}
            </div>
        </Page>
    );
};

export default TopTrends;
